import { Link } from "react-router-dom";
import { Phone, MapPin, Mail } from "lucide-react";
import { CONTACT, IMAGES } from "@/lib/data";
import { useI18n } from "@/i18n";

const FOOTER_NAV = [
  { to: "/", key: "nav.home" },
  { to: "/business-waste-service", key: "nav.business" },
  { to: "/dumpster-rentals", key: "nav.dumpsters" },
  { to: "/recycling", key: "nav.recycling" },
  { to: "/savings-calculator", key: "nav.calculator" },
  { to: "/who-we-are", key: "nav.about" },
  { to: "/contact", key: "nav.contact" },
];

export default function Footer() {
  const { t, localePath } = useI18n();
  return (
    <footer className="relative z-[2] bg-[#0E3B3B] text-white pt-16 pb-28 sm:pb-8">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-[1.5fr_1fr_1.2fr] gap-10 pb-12">
          <div>
            <div className="flex items-center gap-2.5 mb-5">
              <img loading="lazy" decoding="async" src={IMAGES.logoBadge} alt="EWS logo" className="w-10 h-10 rounded-lg" />
              <span className="font-display font-extrabold text-lg tracking-tight text-white">
                EWS<span className="text-emerald-400">.</span>
              </span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-sm">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h4 className="font-mono-tech text-xs uppercase tracking-widest text-emerald-400 mb-5">{t("footer.explore")}</h4>
            <ul className="space-y-3">
              {FOOTER_NAV.map((l) => (
                <li key={l.to}>
                  <Link to={localePath(l.to)} className="text-white/75 text-sm hover:text-emerald-300 transition-colors">
                    {t(l.key)}
                  </Link>
                </li>
              ))}
              <li>
                <Link to={localePath("/service-areas")} className="text-white/75 text-sm hover:text-emerald-300 transition-colors" data-testid="footer-service-areas-link">
                  {t("footer.serviceAreas")}
                </Link>
              </li>
              <li>
                <Link to={localePath("/blog")} className="text-white/75 text-sm hover:text-emerald-300 transition-colors" data-testid="footer-blog-link">
                  {t("footer.blog")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono-tech text-xs uppercase tracking-widest text-emerald-400 mb-5">{t("footer.talk")}</h4>
            <a href={CONTACT.phoneHref} className="flex items-center gap-2.5 text-white font-display font-bold text-xl hover:text-emerald-300 transition-colors" data-testid="phone-cta-footer">
              <Phone size={18} className="text-emerald-400" /> {CONTACT.phone}
            </a>
            <div className="mt-3 flex items-center gap-2.5 text-white/70 text-sm">
              <Mail size={16} className="text-emerald-400" /> {CONTACT.email}
            </div>
            <div className="mt-3 flex items-start gap-2.5 text-white/70 text-sm">
              <MapPin size={16} className="text-emerald-400 mt-0.5" /> {CONTACT.address}
            </div>
            <Link to={localePath("/quick-quote")} className="mt-6 inline-block px-6 py-3 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold text-sm hover:bg-emerald-400 transition-colors">
              {t("footer.requestQuote")}
            </Link>
          </div>
        </div>

        <div className="pt-8 border-t border-white/15 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono-tech text-xs text-white/50">© 2026 {CONTACT.company}™ — All Rights Reserved</p>
          <p className="font-mono-tech text-xs text-white/40">{t("footer.services")}</p>
        </div>
      </div>
    </footer>
  );
}
